// Replay scrubber.
//
// Click "Replay" in the ContextBar → enters REPLAY mode: page tints
// blue, a giant "EXIT REPLAY" affordance shows, and a timeline
// scrubber appears at the bottom. Drag the scrubber to rewind.
//
// While in replay, the live tick pauses (so the dispatcher's view
// freezes), and viewTick can be set anywhere in [0, liveTick]. Cards
// and the board derive their displayed values from viewTick, so bag
// scans visually rewind, hot timers grow back, etc.

import React, { useEffect } from 'react';
import { Box, Typography, Button, Slider, Tooltip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FastForwardIcon from '@mui/icons-material/FastForward';
import { useIntel } from './IntelligenceContext';
import { C } from '../styles';

export function ReplayButton() {
  const { startReplay, replayActive, liveTick } = useIntel();
  if (replayActive) return null;
  return (
    <Tooltip title={liveTick < 30 ? 'Need at least 30s of demo time before replay is useful' : 'Replay last actions'}>
      <Button
        onClick={startReplay}
        startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
        size="small"
        sx={{
          color: C.textSecondary,
          border: `1px solid ${C.border}`,
          borderRadius: 0.6,
          textTransform: 'uppercase',
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: 0.5,
          px: 1, py: 0.4, minWidth: 0,
          '&:hover': { color: '#00BCD4', borderColor: '#00BCD4', backgroundColor: 'rgba(0, 188, 212, 0.06)' },
        }}
      >
        Replay
      </Button>
    </Tooltip>
  );
}

// The full replay overlay — only renders when replay is active.
export function ReplayOverlay() {
  const intel = useIntel();
  const { replayActive, exitReplay, liveTick, demoTick, setReplayTick } = intel;

  // ESC exits
  useEffect(() => {
    if (!replayActive) return;
    const onKey = (e) => { if (e.key === 'Escape') exitReplay(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [replayActive, exitReplay]);

  if (!replayActive) return null;

  const offsetSec = liveTick - demoTick; // how far back from "now"
  const offsetLabel = formatOffset(offsetSec);

  return (
    <>
      {/* Blue tint over the entire view to make REPLAY mode unmistakable */}
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundColor: 'rgba(0, 188, 212, 0.06)',
        pointerEvents: 'none',
        zIndex: 25,
        boxShadow: 'inset 0 0 0 3px rgba(0, 188, 212, 0.5)',
      }} />

      {/* Top banner — labels mode + offset + exit button */}
      <Box
        data-intel-overlay
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 26,
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 1.5, py: 0.85,
          backgroundColor: 'rgba(0, 188, 212, 0.95)',
          color: '#000',
          borderRadius: 1,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5), 0 0 0 2px rgba(0, 188, 212, 0.4)',
        }}
      >
        <HistoryIcon sx={{ fontSize: 18 }} />
        <Box>
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', lineHeight: 1 }}>
            Replay
          </Typography>
          <Typography sx={{
            fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.1,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {offsetSec === 0 ? 'Live position' : `−${offsetLabel} ago`}
          </Typography>
        </Box>
        <Button
          onClick={exitReplay}
          startIcon={<ExitToAppIcon sx={{ fontSize: 14 }} />}
          size="small"
          sx={{
            backgroundColor: '#000', color: '#00BCD4',
            border: '1px solid #000',
            textTransform: 'uppercase', fontWeight: 800, fontSize: '0.6rem', letterSpacing: 0.6,
            '&:hover': { backgroundColor: '#111' },
          }}
        >
          Exit Replay
        </Button>
      </Box>

      {/* Bottom scrubber */}
      <Box
        data-intel-overlay
        onMouseDown={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          bottom: 16, left: 60, right: 60,
          zIndex: 26,
          backgroundColor: 'rgba(13, 16, 25, 0.97)',
          border: '1px solid rgba(0, 188, 212, 0.5)',
          borderRadius: 1,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.6)',
          px: 2, py: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Tooltip title="Jump back 30s">
            <Button
              onClick={() => setReplayTick(Math.max(0, demoTick - 30))}
              size="small"
              sx={{ minWidth: 0, color: C.textSecondary, fontSize: '0.65rem', '&:hover': { color: '#00BCD4' } }}
            >
              -30s
            </Button>
          </Tooltip>
          <Tooltip title="Jump back 5s">
            <Button
              onClick={() => setReplayTick(Math.max(0, demoTick - 5))}
              size="small"
              sx={{ minWidth: 0, color: C.textSecondary, fontSize: '0.65rem', '&:hover': { color: '#00BCD4' } }}
            >
              -5s
            </Button>
          </Tooltip>
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Drag to scrub · {liveTick}s recorded
            </Typography>
          </Box>
          <Tooltip title="Jump forward 5s">
            <Button
              onClick={() => setReplayTick(Math.min(liveTick, demoTick + 5))}
              size="small"
              sx={{ minWidth: 0, color: C.textSecondary, fontSize: '0.65rem', '&:hover': { color: '#00BCD4' } }}
            >
              +5s
            </Button>
          </Tooltip>
          <Tooltip title="Jump to now">
            <Button
              onClick={() => setReplayTick(liveTick)}
              startIcon={<FastForwardIcon sx={{ fontSize: 14 }} />}
              size="small"
              sx={{ minWidth: 0, color: C.textSecondary, fontSize: '0.65rem', '&:hover': { color: '#00BCD4' } }}
            >
              Now
            </Button>
          </Tooltip>
        </Box>
        <Slider
          value={demoTick}
          min={0}
          max={Math.max(liveTick, 1)}
          step={1}
          onChange={(_, v) => setReplayTick(v)}
          sx={{
            color: '#00BCD4',
            height: 6,
            '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.1)', opacity: 1 },
            '& .MuiSlider-track': { backgroundColor: '#00BCD4', border: 'none' },
            '& .MuiSlider-thumb': {
              width: 18, height: 18,
              backgroundColor: '#00BCD4',
              border: '2px solid #0a0d14',
              boxShadow: '0 0 0 4px rgba(0, 188, 212, 0.25)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(0, 188, 212, 0.35)',
              },
            },
          }}
        />
      </Box>
    </>
  );
}

function formatOffset(s) {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}
